import React from 'react';
import { Server, ArrowRight, Shield, Clock, Database, Sparkles } from 'lucide-react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import Button from '../common/Button';

const VmwareMigrationSection = () => {
  const { t } = useTranslatedContent('vmware-migration');

  // Safely get benefits items with fallback
  const benefitItems = t('benefits.items', { returnObjects: true }) || [];
  const benefits = Array.isArray(benefitItems) ? benefitItems : [];

  return (
    <div className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 backdrop-blur-sm mb-8">
              <Sparkles className="h-4 w-4 text-blue-400 mr-2" />
              <span className="text-blue-200 text-sm font-medium">{t('badge')}</span>
            </div>
            
            <h2 className="text-4xl font-bold text-white mb-6">
              {t('title')}
            </h2>
            
            <p className="text-lg text-blue-100 mb-8">
              {t('description')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="text-4xl font-bold text-blue-400 mb-2">
                  {t('stats.cost.value')}
                </div>
                <div className="text-sm text-blue-200">
                  {t('stats.cost.label')}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="text-4xl font-bold text-blue-400 mb-2">
                  {t('stats.dataLoss.value')}
                </div>
                <div className="text-sm text-blue-200">
                  {t('stats.dataLoss.label')}
                </div>
              </div>
            </div>

            <Button 
              variant="secondary"
              icon={ArrowRight}
              className="bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 hover:from-blue-600 hover:via-indigo-600 hover:to-violet-600 text-white border-0"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('cta')}
            </Button>
          </div>

          <div className="space-y-6">
            {/* Process Steps */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <Server className="h-6 w-6 text-blue-400 mr-3" />
                {t('process.title')}
              </h3>
              <div className="space-y-6">
                <div className="flex items-start group">
                  <Clock className="h-5 w-5 text-blue-400 mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <div className="text-blue-100 font-medium group-hover:text-white transition-colors duration-300">
                      {t('process.steps.assessment.title')}
                    </div>
                    <div className="text-blue-200/70 text-sm mt-1 group-hover:text-blue-100 transition-colors duration-300">
                      {t('process.steps.assessment.description')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start group">
                  <Shield className="h-5 w-5 text-blue-400 mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <div className="text-blue-100 font-medium group-hover:text-white transition-colors duration-300">
                      {t('process.steps.migration.title')}
                    </div>
                    <div className="text-blue-200/70 text-sm mt-1 group-hover:text-blue-100 transition-colors duration-300">
                      {t('process.steps.migration.description')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start group">
                  <Database className="h-5 w-5 text-blue-400 mt-1 mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <div className="text-blue-100 font-medium group-hover:text-white transition-colors duration-300">
                      {t('process.steps.verification.title')}
                    </div>
                    <div className="text-blue-200/70 text-sm mt-1 group-hover:text-blue-100 transition-colors duration-300">
                      {t('process.steps.verification.description')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-semibold text-white mb-6">
                {t('benefits.title')}
              </h3>
              <ul className="space-y-4">
                {benefits.map((benefit: string, index: number) => (
                  <li key={index} className="flex items-center group">
                    <ArrowRight className="h-5 w-5 mr-3 text-blue-400 group-hover:translate-x-1 transition-transform duration-300" />
                    <span className="text-blue-100 group-hover:text-white transition-colors duration-300">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VmwareMigrationSection;